import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Menu,
  Badge,
  Avatar,
  Slider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Send as SendIcon,
  Clear as ClearIcon,
  History as HistoryIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  breakdanceService,
  discoCultureService,
  stockPhotoTechnologyService,
  urbanBikeAutoLifeService,
  newNicheAgentsSessionService
} from '../services/newNicheAgentsService';
import {
  NEW_NICHE_AGENTS,
  MoveCategory,
  MediaType,
  DetailLevel,
  DiscoEra,
  DiscoMediaType,
  SocioeconomicLevel,
  TechnologyFocus,
  TechnologySubsector,
  StockPhotoStyle,
  UsageType,
  QualityLevel,
  LightingMood,
  Orientation,
  UrbanCultureType,
  UrbanMediaType
} from '../models/newNicheAgents';

const NewNicheAgentIterator: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSession, setCurrentSession] = useState<any>(null);
  const [sessionMenuAnchor, setSessionMenuAnchor] = useState<null | HTMLElement>(null);
  const [savedSessions, setSavedSessions] = useState<string[]>([]);
  
  // Common fields
  const [baseDescription, setBaseDescription] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [enhancementFocus, setEnhancementFocus] = useState('');
  
  // Breakdance specific fields
  const [moveCategory, setMoveCategory] = useState<MoveCategory>(MoveCategory.TopRock);
  const [specificMove, setSpecificMove] = useState('');
  const [mediaType, setMediaType] = useState<MediaType>(MediaType.ActionShot);
  const [anatomicalDetail, setAnatomicalDetail] = useState<DetailLevel>(DetailLevel.Moderate);
  const [physicsDetail, setPhysicsDetail] = useState<DetailLevel>(DetailLevel.Moderate);
  const [movementSpeed, setMovementSpeed] = useState('');
  const [powerLevel, setPowerLevel] = useState('');
  
  // Disco Culture specific fields
  const [discoEra, setDiscoEra] = useState<DiscoEra>(DiscoEra.ClassicDisco);
  const [discoMediaType, setDiscoMediaType] = useState<DiscoMediaType>(DiscoMediaType.DanceFloor);
  const [city, setCity] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [demographics, setDemographics] = useState('');
  const [socioeconomicLevel, setSocioeconomicLevel] = useState<SocioeconomicLevel>(SocioeconomicLevel.Mixed);
  const [includeAuthenticDemographics, setIncludeAuthenticDemographics] = useState(true);
  
  // Stock Photo Technology specific fields
  const [technologyFocus, setTechnologyFocus] = useState<TechnologyFocus>(TechnologyFocus.AI);
  const [technologySubsectors, setTechnologySubsectors] = useState<TechnologySubsector[]>([]);
  const [stockPhotoStyle, setStockPhotoStyle] = useState<StockPhotoStyle>(StockPhotoStyle.Modern);
  const [usageType, setUsageType] = useState<UsageType>(UsageType.Commercial);
  const [qualityLevel, setQualityLevel] = useState<QualityLevel>(QualityLevel.Premium);
  const [lightingMood, setLightingMood] = useState<LightingMood>(LightingMood.Natural);
  const [orientation, setOrientation] = useState<Orientation>(Orientation.Landscape);
  
  // Urban Bike Auto Life specific fields
  const [cultureType, setCultureType] = useState<UrbanCultureType>(UrbanCultureType.BikeLife);
  const [urbanMediaType, setUrbanMediaType] = useState<UrbanMediaType>(UrbanMediaType.ActionShot);
  const [era, setEra] = useState('');
  const [includeSocioeconomicContext, setIncludeSocioeconomicContext] = useState(true);
  
  const agentConfig = NEW_NICHE_AGENTS.find(a => a.id === agentId);
  
  useEffect(() => {
    loadSavedSessions();
  }, [agentId]);
  
  const loadSavedSessions = () => {
    const sessionIds = newNicheAgentsSessionService.getSessionsByAgent(agentId || '');
    setSavedSessions(sessionIds);
  };
  
  const handleGenerate = async () => {
    if (!baseDescription.trim()) {
      setError('Please provide a base description');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      switch (agentId) {
        case 'breakdance':
          response = await breakdanceService.generate({
            baseDescription,
            moveCategory,
            specificMove: specificMove || undefined,
            mediaType,
            anatomicalDetail,
            physicsDetail,
            movementSpeed: movementSpeed || undefined,
            powerLevel: powerLevel || undefined
          });
          break;
          
        case 'culture-disco':
          response = await discoCultureService.generate({
            baseDescription,
            discoEra,
            mediaType: discoMediaType,
            city: city || undefined,
            neighborhood: neighborhood || undefined,
            demographics: demographics || undefined,
            socioeconomicLevel,
            includeAuthenticDemographics
          });
          break;
          
        case 'stock-photo-technology':
          response = await stockPhotoTechnologyService.generate({
            baseDescription,
            technologyFocus,
            technologySubsectors: technologySubsectors.length > 0 ? technologySubsectors : undefined,
            stockPhotoStyle,
            usageType,
            qualityLevel,
            lightingMood,
            orientation
          });
          break;
          
        case 'urban-bike-auto-life':
          response = await urbanBikeAutoLifeService.generate({
            baseDescription,
            cultureType,
            mediaType: urbanMediaType,
            city: city || undefined,
            neighborhood: neighborhood || undefined,
            era: era || undefined,
            demographics: demographics || undefined,
            includeSocioeconomicContext
          });
          break;
          
        default:
          throw new Error('Unknown agent type');
      }
      
      setCurrentSession({ ...response, agentType: agentId });
      loadSavedSessions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate prompt');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEnhance = async () => {
    if (!currentSession || !enhancementFocus.trim()) {
      setError('Please provide enhancement focus');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let iteration;
      const baseRequest = {
        enhancementFocus,
        temperature,
        maxTokens
      };
      
      switch (agentId) {
        case 'breakdance':
          iteration = await breakdanceService.enhance(currentSession.sessionId, baseRequest);
          break;
          
        case 'culture-disco':
          iteration = await discoCultureService.enhance(currentSession.sessionId, {
            ...baseRequest,
            additionalContext: demographics
          });
          break;
          
        case 'stock-photo-technology':
          iteration = await stockPhotoTechnologyService.enhance(currentSession.sessionId, {
            ...baseRequest,
            technicalFocus: specificMove // Reusing field for technical focus
          });
          break;
          
        case 'urban-bike-auto-life':
          iteration = await urbanBikeAutoLifeService.enhance(currentSession.sessionId, {
            ...baseRequest,
            culturalDepth: demographics
          });
          break;
          
        default:
          throw new Error('Unknown agent type');
      }
      
      const updatedSession = {
        ...currentSession,
        currentPrompt: iteration.generatedPrompt,
        iterations: [...currentSession.iterations, iteration]
      };
      
      setCurrentSession(updatedSession);
      setEnhancementFocus('');
      newNicheAgentsSessionService.saveSession(currentSession.sessionId, updatedSession);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to enhance prompt');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLoadSession = (sessionId: string) => {
    const session = newNicheAgentsSessionService.getSession(sessionId);
    if (session) {
      setCurrentSession(session);
    }
    setSessionMenuAnchor(null);
  };
  
  const handleDeleteSession = (sessionId: string) => {
    newNicheAgentsSessionService.deleteSession(sessionId);
    loadSavedSessions();
    if (currentSession?.sessionId === sessionId) {
      setCurrentSession(null);
    }
  };
  
  const handleCopyPrompt = (text: string) => {
    navigator.clipboard.writeText(text);
  };
  
  const handleDownloadSession = () => {
    if (!currentSession) return;
    
    const content = JSON.stringify(currentSession, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${agentId}_session_${currentSession.sessionId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  if (!agentConfig) {
    return (
      <Container>
        <Alert severity="error">Unknown agent type</Alert>
        <Button onClick={() => navigate('/niche-agents')}>Back to Gallery</Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/niche-agents')}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ backgroundColor: agentConfig.color, fontSize: '24px' }}>
            {agentConfig.icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4">{agentConfig.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {agentConfig.description}
            </Typography>
          </Box>
          
          <IconButton onClick={(e) => setSessionMenuAnchor(e.currentTarget)}>
            <Badge badgeContent={savedSessions.length} color="primary">
              <HistoryIcon />
            </Badge>
          </IconButton>
        </Stack>
        
        <Menu
          anchorEl={sessionMenuAnchor}
          open={Boolean(sessionMenuAnchor)}
          onClose={() => setSessionMenuAnchor(null)}
        >
          {savedSessions.length === 0 ? (
            <MenuItem disabled>No saved sessions</MenuItem>
          ) : (
            savedSessions.map((sessionId) => (
              <MenuItem key={sessionId}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }} onClick={() => handleLoadSession(sessionId)}>
                    <Typography variant="body2" noWrap>
                      Session {sessionId.substring(0, 8)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSession(sessionId);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </MenuItem>
            ))
          )}
        </Menu>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {!currentSession && (
          <>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Base Description"
              value={baseDescription}
              onChange={(e) => setBaseDescription(e.target.value)}
              placeholder={`Describe the ${agentConfig.name.toLowerCase()} scene you want to create...`}
              sx={{ mb: 2 }}
            />
            
            {/* Agent-specific fields */}
            {agentId === 'breakdance' && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Move Category</InputLabel>
                    <Select
                      value={moveCategory}
                      onChange={(e) => setMoveCategory(e.target.value as MoveCategory)}
                      label="Move Category"
                    >
                      <MenuItem value={MoveCategory.TopRock}>Top Rock</MenuItem>
                      <MenuItem value={MoveCategory.Footwork}>Footwork</MenuItem>
                      <MenuItem value={MoveCategory.PowerMoves}>Power Moves</MenuItem>
                      <MenuItem value={MoveCategory.Freezes}>Freezes</MenuItem>
                      <MenuItem value={MoveCategory.Transitions}>Transitions</MenuItem>
                      <MenuItem value={MoveCategory.Floorwork}>Floorwork</MenuItem>
                      <MenuItem value={MoveCategory.Tricks}>Tricks</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Specific Move"
                    value={specificMove}
                    onChange={(e) => setSpecificMove(e.target.value)}
                    placeholder="e.g., Windmill, Headspin"
                  />
                  
                  <FormControl fullWidth>
                    <InputLabel>Media Type</InputLabel>
                    <Select
                      value={mediaType}
                      onChange={(e) => setMediaType(e.target.value as MediaType)}
                      label="Media Type"
                    >
                      <MenuItem value={MediaType.StillImage}>Still Image</MenuItem>
                      <MenuItem value={MediaType.ActionShot}>Action Shot</MenuItem>
                      <MenuItem value={MediaType.VideoFrame}>Video Frame</MenuItem>
                      <MenuItem value={MediaType.SequentialFrames}>Sequential Frames</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Anatomical Detail</InputLabel>
                    <Select
                      value={anatomicalDetail}
                      onChange={(e) => setAnatomicalDetail(e.target.value as DetailLevel)}
                      label="Anatomical Detail"
                    >
                      <MenuItem value={DetailLevel.Basic}>Basic</MenuItem>
                      <MenuItem value={DetailLevel.Moderate}>Moderate</MenuItem>
                      <MenuItem value={DetailLevel.High}>High</MenuItem>
                      <MenuItem value={DetailLevel.Expert}>Expert</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Physics Detail</InputLabel>
                    <Select
                      value={physicsDetail}
                      onChange={(e) => setPhysicsDetail(e.target.value as DetailLevel)}
                      label="Physics Detail"
                    >
                      <MenuItem value={DetailLevel.Basic}>Basic</MenuItem>
                      <MenuItem value={DetailLevel.Moderate}>Moderate</MenuItem>
                      <MenuItem value={DetailLevel.High}>High</MenuItem>
                      <MenuItem value={DetailLevel.Expert}>Expert</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            )}
            
            {agentId === 'culture-disco' && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Disco Era</InputLabel>
                    <Select
                      value={discoEra}
                      onChange={(e) => setDiscoEra(e.target.value as DiscoEra)}
                      label="Disco Era"
                    >
                      <MenuItem value={DiscoEra.EarlyDisco}>Early Disco (1970-1974)</MenuItem>
                      <MenuItem value={DiscoEra.ClassicDisco}>Classic Disco (1975-1979)</MenuItem>
                      <MenuItem value={DiscoEra.LateDisco}>Late Disco (1980-1984)</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Media Type</InputLabel>
                    <Select
                      value={discoMediaType}
                      onChange={(e) => setDiscoMediaType(e.target.value as DiscoMediaType)}
                      label="Media Type"
                    >
                      <MenuItem value={DiscoMediaType.DanceFloor}>Dance Floor</MenuItem>
                      <MenuItem value={DiscoMediaType.NightclubScene}>Nightclub Scene</MenuItem>
                      <MenuItem value={DiscoMediaType.Fashion}>Fashion</MenuItem>
                      <MenuItem value={DiscoMediaType.Lifestyle}>Lifestyle</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Socioeconomic Level</InputLabel>
                    <Select
                      value={socioeconomicLevel}
                      onChange={(e) => setSocioeconomicLevel(e.target.value as SocioeconomicLevel)}
                      label="Socioeconomic Level"
                    >
                      <MenuItem value={SocioeconomicLevel.WorkingClass}>Working Class</MenuItem>
                      <MenuItem value={SocioeconomicLevel.MiddleClass}>Middle Class</MenuItem>
                      <MenuItem value={SocioeconomicLevel.UpperClass}>Upper Class</MenuItem>
                      <MenuItem value={SocioeconomicLevel.Mixed}>Mixed</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="e.g., New York, Los Angeles"
                  />
                  
                  <TextField
                    fullWidth
                    label="Neighborhood"
                    value={neighborhood}
                    onChange={(e) => setNeighborhood(e.target.value)}
                    placeholder="e.g., Studio 54, The Bronx"
                  />
                </Stack>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeAuthenticDemographics}
                      onChange={(e) => setIncludeAuthenticDemographics(e.target.checked)}
                    />
                  }
                  label="Include Authentic Demographics"
                />
              </Stack>
            )}
            
            {agentId === 'stock-photo-technology' && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Technology Focus</InputLabel>
                    <Select
                      value={technologyFocus}
                      onChange={(e) => setTechnologyFocus(e.target.value as TechnologyFocus)}
                      label="Technology Focus"
                    >
                      <MenuItem value={TechnologyFocus.AI}>AI</MenuItem>
                      <MenuItem value={TechnologyFocus.CloudComputing}>Cloud Computing</MenuItem>
                      <MenuItem value={TechnologyFocus.Cybersecurity}>Cybersecurity</MenuItem>
                      <MenuItem value={TechnologyFocus.DataScience}>Data Science</MenuItem>
                      <MenuItem value={TechnologyFocus.IoT}>IoT</MenuItem>
                      <MenuItem value={TechnologyFocus.Blockchain}>Blockchain</MenuItem>
                      <MenuItem value={TechnologyFocus.QuantumComputing}>Quantum Computing</MenuItem>
                      <MenuItem value={TechnologyFocus.AR_VR}>AR/VR</MenuItem>
                      <MenuItem value={TechnologyFocus.Robotics}>Robotics</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Photo Style</InputLabel>
                    <Select
                      value={stockPhotoStyle}
                      onChange={(e) => setStockPhotoStyle(e.target.value as StockPhotoStyle)}
                      label="Photo Style"
                    >
                      <MenuItem value={StockPhotoStyle.Corporate}>Corporate</MenuItem>
                      <MenuItem value={StockPhotoStyle.Modern}>Modern</MenuItem>
                      <MenuItem value={StockPhotoStyle.Futuristic}>Futuristic</MenuItem>
                      <MenuItem value={StockPhotoStyle.Minimalist}>Minimalist</MenuItem>
                      <MenuItem value={StockPhotoStyle.Documentary}>Documentary</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Orientation</InputLabel>
                    <Select
                      value={orientation}
                      onChange={(e) => setOrientation(e.target.value as Orientation)}
                      label="Orientation"
                    >
                      <MenuItem value={Orientation.Landscape}>Landscape</MenuItem>
                      <MenuItem value={Orientation.Portrait}>Portrait</MenuItem>
                      <MenuItem value={Orientation.Square}>Square</MenuItem>
                      <MenuItem value={Orientation.Panoramic}>Panoramic</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Quality Level</InputLabel>
                    <Select
                      value={qualityLevel}
                      onChange={(e) => setQualityLevel(e.target.value as QualityLevel)}
                      label="Quality Level"
                    >
                      <MenuItem value={QualityLevel.Standard}>Standard</MenuItem>
                      <MenuItem value={QualityLevel.Premium}>Premium</MenuItem>
                      <MenuItem value={QualityLevel.UltraHigh}>Ultra High</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Lighting Mood</InputLabel>
                    <Select
                      value={lightingMood}
                      onChange={(e) => setLightingMood(e.target.value as LightingMood)}
                      label="Lighting Mood"
                    >
                      <MenuItem value={LightingMood.Bright}>Bright</MenuItem>
                      <MenuItem value={LightingMood.Moody}>Moody</MenuItem>
                      <MenuItem value={LightingMood.Natural}>Natural</MenuItem>
                      <MenuItem value={LightingMood.Dramatic}>Dramatic</MenuItem>
                      <MenuItem value={LightingMood.Clinical}>Clinical</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            )}
            
            {agentId === 'urban-bike-auto-life' && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Culture Type</InputLabel>
                    <Select
                      value={cultureType}
                      onChange={(e) => setCultureType(e.target.value as UrbanCultureType)}
                      label="Culture Type"
                    >
                      <MenuItem value={UrbanCultureType.BikeLife}>Bike Life</MenuItem>
                      <MenuItem value={UrbanCultureType.AutoCulture}>Auto Culture</MenuItem>
                      <MenuItem value={UrbanCultureType.StreetRacing}>Street Racing</MenuItem>
                      <MenuItem value={UrbanCultureType.Lowriders}>Lowriders</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Media Type</InputLabel>
                    <Select
                      value={urbanMediaType}
                      onChange={(e) => setUrbanMediaType(e.target.value as UrbanMediaType)}
                      label="Media Type"
                    >
                      <MenuItem value={UrbanMediaType.ActionShot}>Action Shot</MenuItem>
                      <MenuItem value={UrbanMediaType.GroupPhoto}>Group Photo</MenuItem>
                      <MenuItem value={UrbanMediaType.DetailShot}>Detail Shot</MenuItem>
                      <MenuItem value={UrbanMediaType.SceneShot}>Scene Shot</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="e.g., Miami, Los Angeles"
                  />
                  
                  <TextField
                    fullWidth
                    label="Era"
                    value={era}
                    onChange={(e) => setEra(e.target.value)}
                    placeholder="e.g., 90s, Modern"
                  />
                </Stack>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeSocioeconomicContext}
                      onChange={(e) => setIncludeSocioeconomicContext(e.target.checked)}
                    />
                  }
                  label="Include Socioeconomic Context"
                />
              </Stack>
            )}
            
            <Box sx={{ my: 2 }}>
              <Typography gutterBottom>Temperature: {temperature}</Typography>
              <Slider
                value={temperature}
                onChange={(_, value) => setTemperature(value as number)}
                min={0}
                max={1}
                step={0.1}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
            
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleGenerate}
              disabled={loading || !baseDescription.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ 
                backgroundColor: agentConfig.color,
                '&:hover': {
                  backgroundColor: agentConfig.color,
                  filter: 'brightness(0.9)'
                }
              }}
            >
              {loading ? 'Generating...' : 'Generate Prompt'}
            </Button>
          </>
        )}
      </Paper>
      
      {currentSession && (
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h5">
              Session: {currentSession.sessionId.substring(0, 8)}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Download session">
                <IconButton onClick={handleDownloadSession}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setCurrentSession(null);
                  setBaseDescription('');
                  setEnhancementFocus('');
                }}
              >
                New Session
              </Button>
            </Stack>
          </Stack>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Current Prompt
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="start">
                  <Typography variant="body1" sx={{ flexGrow: 1 }}>
                    {currentSession.currentPrompt}
                  </Typography>
                  <Tooltip title="Copy prompt">
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopyPrompt(currentSession.currentPrompt)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          </Box>
          
          {currentSession.iterations.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Iteration History
              </Typography>
              <Stack spacing={2}>
                {currentSession.iterations.map((iteration: any, index: number) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Chip 
                          label={`Iteration ${iteration.iterationNumber}`} 
                          size="small" 
                          color="primary"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(iteration.timestamp).toLocaleString()}
                        </Typography>
                      </Stack>
                      {iteration.enhancementFocus && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Enhancement Focus: {iteration.enhancementFocus}
                        </Alert>
                      )}
                      <Typography variant="body2">
                        {iteration.generatedPrompt}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
          
          <Box>
            <Typography variant="h6" gutterBottom>
              Continue Enhancing
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Enhancement Focus"
              value={enhancementFocus}
              onChange={(e) => setEnhancementFocus(e.target.value)}
              placeholder="What aspect would you like to enhance?"
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleEnhance}
              disabled={loading || !enhancementFocus.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              sx={{ 
                backgroundColor: agentConfig.color,
                '&:hover': {
                  backgroundColor: agentConfig.color,
                  filter: 'brightness(0.9)'
                }
              }}
            >
              {loading ? 'Enhancing...' : 'Enhance Prompt'}
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default NewNicheAgentIterator;